// src/helpers/setDefaultUserForIdentity.ts

import { ApiClient } from '@center-technologies/shared-api-clients';

export class SetDefaultUserError extends Error {
  success: boolean;
  statusCode: number;
  response: any;

  constructor(message: string, statusCode: number, response: any) {
    super(message);
    this.name = 'SetDefaultUserError';
    this.success = false;
    this.statusCode = statusCode;
    this.response = response;
  }
}

export interface SetDefaultUserResult {
  success: true;
  statusCode: number;
  message: string;
}

/**
 * Sets the default user for a given identity via PUT /identities/:id/default-user.
 *
 * @param apiClient       – an ApiClient bound to your Graph endpoint
 * @param identityId      – the identity’s UUID
 * @param defaultUserId   – the user’s UUID to mark as default
 * @returns resolvable result on success
 * @throws SetDefaultUserError with details on failure
 */
export async function setDefaultUserForIdentity(
  apiClient: ApiClient,
  identityId: string,
  defaultUserId: string
): Promise<SetDefaultUserResult> {
  if (!identityId?.trim() || !defaultUserId?.trim()) {
    throw new SetDefaultUserError(
      'identityId and defaultUserId are required',
      400,
      null
    );
  }

  try {
    // note: apiClient.put will automatically add the Bearer header
    const data = await apiClient.put<{
      success: boolean;
      message?: string;
    }>(
      `/identities/${encodeURIComponent(identityId)}/default-user`,
      { defaultUserId }
    );

    return {
      success: true,
      statusCode: 200,
      message: data.message ?? 'Default user set successfully',
    };
  } catch (err: any) {
    const status = err.response?.status ?? 500;
    const msg =
      err.response?.data?.error ??
      err.response?.data?.message ??
      err.message ??
      'Failed to set default user';
    throw new SetDefaultUserError(msg, status, err.response);
  }
}
